import type { ModalId } from "@@/modals/context/modal-id";
import { Modal } from "@@/modals/modal";
import { ModalHeader } from "@@/modals/modal-header";
import { useModalActions } from "@@/modals/use-modal-actions";
import { browserLogger } from "@@/settings/browser-logger";
import { Column } from "@@/shared/components/column";
import { EmptyState } from "@@/shared/components/empty-state";
import { Row } from "@@/shared/components/row";
import { VirtualizedList_WithShrinkableContainer } from "@@/shared/components/virtualized-list";
import { HorizontalDivider } from "@@/shared/dividers";
import { CheckBoxWithText, TextInput } from "@@/shared/text/text-input";
import { useTranslate } from "@@/translations/use-translate";
import type { ResourceMap, ResourceMapId, Translatable } from "@towni/common";
import { remSize, translation } from "@towni/common";
import * as React from "react";
import { ResourceMapPickerRow } from "./resource-map-picker-row";

type Props = {
    modalId: ModalId;
    title?: Translatable;
    resourceMaps: ResourceMap[];
    onToggle: (resourceId: ResourceMapId) => void;
    selected: Set<ResourceMapId>;
};

const ResourceMapPickerModal = (props: Props) => {
    const translate = useTranslate();
    const [textFilter, setTextFilter] = React.useState("");
    const [selectedFilter, setSelectedFilter] = React.useState(false);
    const { resourceMaps, selected, modalId } = props;
    const { hide } = useModalActions(props.modalId);
    const filteredResourceMaps = React.useMemo(() => {
        return resourceMaps.filter(resourceMap => {
            // Filter by text value
            const textFilterMatch =
                !textFilter ||
                translate(resourceMap.title)
                    .toLocaleLowerCase()
                    .includes(textFilter.toLocaleLowerCase());

            // Filter if selected
            const selectedFilterMatch =
                !selectedFilter || selected.has(resourceMap._id);

            // Hide all inactive resourceMaps; that are not already selected
            // eslint-disable-next-line no-constant-condition
            if (false) {
                // resource.status.type !== "ACTIVE" && !selectedFilterMatch) {
                return false;
            }

            // TODO! filter by provider names as well
            return textFilterMatch && selectedFilterMatch;
        });
    }, [resourceMaps, textFilter, translate, selectedFilter, selected]);

    const onToggleRef = React.useRef(props.onToggle);
    onToggleRef.current = props.onToggle;
    const renderRow = React.useCallback(
        (index: number, item: ResourceMap) => {
            const isFirst = index === 0;
            const isLast = index === filteredResourceMaps.length - 1;
            return (
                <ResourceMapPickerRow
                    key={item._id}
                    resourceMap={item}
                    toggle={onToggleRef.current}
                    selected={selected.has(item._id)}
                    padding={{
                        top: isFirst ? 20 : 12,
                        bottom: isLast ? 40 : 0,
                    }}
                />
            );
        },
        [filteredResourceMaps.length, selected],
    );

    return (
        <Modal
            modalId={modalId}
            header={
                <ModalHeader
                    modalId={props.modalId}
                    bottomBorder
                    title={
                        props.title ?? {
                            sv: "Välj karta",
                            en: "Select map",
                        }
                    }
                    topRightButton={{
                        _type: "TOP_RIGHT_ACTION_TEXT",
                        text: translation({
                            sv: "Klar",
                            en: "Ready",
                        }),
                        action: () => {
                            hide();
                            setSelectedFilter(false);
                            setTextFilter("");
                        },
                    }}
                />
            }>
            <Column className="border-neutral-outline bg-primary-100 w-full border-b-1 px-5">
                <Row mainAxis="between" crossAxis="center" className="py-5">
                    <TextInput
                        type="text"
                        grow={1}
                        padding={{ leftRight: 15, topBottom: 12 }}
                        size={remSize(0.875)}
                        placeholder={translation({
                            sv: "Filtrera",
                            en: "Filter",
                        })}
                        css={{
                            backgroundColor: "#ffffff",
                            "& input": {
                                backgroundColor: "#ffffff",
                                color: "#000000",
                            },
                        }}
                        initialValue={textFilter}
                        onChange={setTextFilter}
                        onValidationFailed={browserLogger.log}
                    />
                    <HorizontalDivider S />
                    <CheckBoxWithText
                        checked={selectedFilter}
                        text={translation({
                            sv: "Endast valda",
                            en: "Only selected",
                        })}
                        onChange={setSelectedFilter}
                    />
                </Row>
            </Column>
            <Column>
                <VirtualizedList_WithShrinkableContainer
                    minHeight="calc(var(--spacing) * 32)"
                    maxHeight="60vh"
                    data={filteredResourceMaps}
                    itemContent={renderRow}
                    // overscan={200}
                    components={{
                        EmptyPlaceholder: () => (
                            <EmptyState
                                className="m-5 h-22"
                                text={
                                    resourceMaps.length
                                        ? {
                                              sv: "Inga kartor matchar filtret",
                                              en: "No maps match the filter",
                                          }
                                        : {
                                              sv: "Inga kartor",
                                              en: "No maps",
                                          }
                                }
                            />
                        ),
                    }}
                />
            </Column>
        </Modal>
    );
};

export { ResourceMapPickerModal };
